/* ==========================================================================
   The generated read.

   The screen's payoff is an analysis of one owner's answers, and an authored
   paragraph per area can only ever be so specific. So the read is written
   server-side by a model reasoning over the structured answers, under the
   guardrails in quick-look.md, and this file is the client half of that.

   The rule that governs everything here: the generated read is an improvement,
   never a dependency. If the call is slow, fails, has not been built yet, or
   comes back in a shape we did not ask for, `requestRead` returns null and the
   screen falls back to the authored read from quick-look-model.js. An owner
   must never see an error, a blank space, or half a read.

   Wire shape. The route is a `type` in the body rather than a path segment,
   which is how every endpoint on the api is addressed:

     POST /api/v1/quick_look/
     body   { type: 'read', payload: <buildModelPayload output> }
     200    { id, read: { reflection, areas: [{ title, detail, meaning }], action,
                            article?: { minutes, slug, title, topic } } }

   The id is a uuid naming the row the server wrote. It rides along on the read
   and goes back with the contact form, which is how a lead ends up attached to
   the exact words its owner was looking at. The read is never sent back up: the
   server has it, and fetching what it wrote beats trusting what it is handed.

   Anything else — non-200, malformed JSON, fewer than two areas, a missing
   field, an empty string, a price — is treated as no read at all.
   ========================================================================== */

import API from 'files/api.js';

/* Sized to what the read actually costs, which was measured rather than
   guessed: around eight hundred output tokens written at roughly fifty-five a
   second, so twelve to fifteen seconds when nothing is wrong.

   This was fifteen seconds, which sounds generous and was not. It sat inside the
   range of a healthy request, so a normal read raced its own deadline and lost
   about as often as it won, and every loss looked from the outside exactly like
   a working tool showing a slightly duller read.

   It stays above the server's own ceiling so a slow request comes back as an
   answer we control rather than as the browser hanging up mid-flight. On expiry
   the authored read renders instead, which is a worse read but an immediate
   one. */
const TIMEOUT = 34000;

/* Ceilings, not targets — a guard against an essay. Identical to the server's,
   in QuickLook::$read_limits, and they move together: a read the server passes
   and this then drops is one we paid for and never showed. */
const LIMITS = { action: 700, detail: 620, meaning: 620, reflection: 340, title: 90 };

/* The guardrails say never quote, estimate, or imply a price. That instruction
   lives in the server prompt, and this is the second lock on the same door —
   a figure that slipped through would contradict the pricing position on every
   other page of the site, so the whole read is dropped rather than shown. */
const PRICED = /[$£€]\s?\d|\b\d+\s?(k|thousand|million)\b|\bper (hour|month)\b.*\b\d/i;

const clean = (value, limit) => {
    if(typeof value !== 'string') return null;
    const text = value.trim();
    if(!text || text.length > limit || PRICED.test(text)) return null;
    return text;
};

/* Partial output is worse than no output: two good areas and a missing action
   would leave the screen without the one thing the owner can act on. So this
   is all or nothing. */
const validate = read => {

    if(!read || typeof read !== 'object') return null;

    const areas = (Array.isArray(read.areas) ? read.areas : [])
        .map(area => ({
            title: clean(area && area.title, LIMITS.title),
            detail: clean(area && area.detail, LIMITS.detail),
            meaning: clean(area && area.meaning, LIMITS.meaning)
        }))
        .filter(area => area.title && area.detail && area.meaning)
        .slice(0, 3);

    const action = clean(read.action, LIMITS.action);
    const reflection = clean(read.reflection, LIMITS.reflection);

    if(areas.length < 2 || !action || !reflection) return null;

    return { action: action, areas: areas, article: article(read.article), reflection: reflection };
};

/* One piece of ours worth reading, chosen by the model and already resolved to
   a real row on the server — the model only ever names which one, so an invented
   slug never reaches here. Everything below is the row as we hold it, which is
   why it arrives whole rather than as a title to look up.

   The exception to the all-or-nothing rule above: the read stands without a
   suggestion, so one in a shape we did not expect is dropped on its own rather
   than taking the read with it.

   `topic` and `minutes` are the card's furniture and not worth failing over. A
   piece with no topic recorded still deserves its link. */
const article = value => {

    const slug = value && typeof value.slug === 'string' ? value.slug.trim() : '';
    const title = clean(value && value.title, LIMITS.title);
    const minutes = value && Number.isFinite(value.minutes) ? Math.round(value.minutes) : 0;

    if(!/^[a-z0-9-]{1,160}$/.test(slug) || !title) {
        return null;
    }
    return {
        minutes: minutes > 0 && minutes < 120 ? minutes : null,
        slug: slug,
        title: title,
        topic: clean(value.topic, LIMITS.title)
    };
};

// opaque to us; it only has to survive the round trip intact
const identifier = value => (typeof value === 'string' && /^[0-9a-f-]{36}$/i.test(value) ? value : null);

export const requestRead = async payload => {

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), TIMEOUT);

    try {
        const response = await fetch(`/api/v${API.version}/quick_look/`, {
            body: JSON.stringify({ payload: payload, type: 'read' }),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
            signal: controller.signal
        });
        if(response.status !== 200) {
            throw new Error(`the server answered ${response.status}`);
        }
        const body = await response.json();
        const read = validate(body && body.read);
        if(!read) {
            throw new Error('the read came back in a shape we did not ask for');
        }
        return { ...read, id: identifier(body && body.id) };

    } catch(e) {
        /* Every failure is the same failure as far as the screen is concerned:
           the authored read renders and the owner is none the wiser, which is
           the whole design. It also means a broken endpoint looks exactly like a
           working one from the outside, so the reason goes to the console — it
           exists nowhere else. */
        console.warn(`Quick Look: showing the authored read — ${e.message}`);
        return null;

    } finally {
        window.clearTimeout(timer);
    }
};
